import type { RewardType } from '@cou/admin-ui';

/**
 * One rung of a ParlayEscalator's boost ladder: at `legs` legs — `=` exactly that many, `>=` that
 * many or more — the parlay's profit is boosted by `percent`. Caesars' sample ladder is
 * 2 legs = 75%, 3 = 85%, 4+ = 100% (`docs/reference/caesars-api/parlay-escalator-payload.md`).
 */
export interface BoostLadderRung {
  readonly legs: number;
  readonly operator: string;
  readonly percent: number;
}

/**
 * A payout-matrix cell (GL-14): reward type plus its adapter-specific value fields. Shared
 * between the detail page's read-only Prizes tab and the editor's payout-matrix editor
 * (CAE-298), so both render the same reward summary from the same stored shape.
 *
 * Cells are **game-shaped, not wire-shaped**: numeric fields are stored as numbers, and the pump
 * stringifies them when it builds `prizeInfo` (CAE-556, `apps/functions/src/awards/build.ts`).
 * The odds fields are the exception — fractional odds like `1/25` have no numeric form, so they
 * are stored and sent as the strings the operator types.
 */
export interface RewardCell {
  readonly type: string;
  readonly amount?: number;
  readonly value?: number;
  readonly maxStake?: number;
  /** ParlayEscalator (`TieredProfitBoost` on the wire): the cap on the boosted return. */
  readonly maxReward?: number;
  /** ParlayEscalator: the fractional odds every leg must clear, e.g. `1/25`. */
  readonly minOddsPerLeg?: string;
  /** ParlayEscalator: the leg-count → boost-percent ladder. */
  readonly boostPercentageLadder?: readonly BoostLadderRung[];
  /** Fractional odds bound on the reward as a whole, e.g. `{ min: '1/2' }`. */
  readonly odds?: { readonly min?: string };
}

/** The stored `type` string ('FreeBet' etc.) as admin-ui's `RewardPill` spells it. `raw` is
 * declared `unknown`, not `string`: the value comes from a jsonb column the backend only
 * validates as "an object" (CAE-298), so a cell missing `type` must not throw during render. */
export function toRewardType(raw: unknown): RewardType | null {
  if (typeof raw !== 'string') return null;
  const key = raw.toLowerCase().replace(/[\s_-]/g, '');
  if (key === 'freebet' || key === 'bonusbet') return 'freebet';
  if (key === 'profitboost') return 'profitboost';
  if (key === 'parlay' || key === 'parlayescalator') return 'parlay';
  return null;
}

export function formatRewardValue(cell: RewardCell): string {
  if (cell.amount != null) return `$${cell.amount}`;
  // A ParlayEscalator pays a range of boosts rather than one, so the grid shows the ladder's
  // span. `max` is the stake cap in both cases, the same field ProfitBoost renders.
  if (cell.boostPercentageLadder != null && cell.boostPercentageLadder.length > 0) {
    const percents = cell.boostPercentageLadder.map((rung) => rung.percent);
    const low = Math.min(...percents);
    const high = Math.max(...percents);
    const range = low === high ? `${low}%` : `${low}–${high}%`;
    return `${range}${capSuffix(cell)}`;
  }
  if (cell.value != null) return `${cell.value}%${capSuffix(cell)}`;
  return '';
}

function capSuffix(cell: RewardCell): string {
  return cell.maxStake != null ? ` · max $${cell.maxStake}` : '';
}
